import React, { createContext, useContext, ReactNode } from 'react';
import { useRealtimeMarketData } from '@/hooks/useRealtimeMarketData';

interface MarketDataContextType {
  marketData: any | null;
  alerts: any[];
  isConnected: boolean;
  error: string | null;
  connect: () => void;
  disconnect: () => void;
  subscribeToRates: () => void;
  subscribeToTrends: (zipCode: string) => void;
  requestPropertyAlerts: (criteria: any) => void;
  getInterestRateForLoanType: (loanType: 'conventional' | 'fha' | 'va' | 'usda') => number;
}

const MarketDataContext = createContext<MarketDataContextType | undefined>(undefined);

export const MarketDataProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const marketDataHook = useRealtimeMarketData();
  
  const getInterestRateForLoanType = (loanType: 'conventional' | 'fha' | 'va' | 'usda'): number => {
    if (!marketDataHook.marketData) {
      return 6.5; // Fallback to default if no market data
    }
    
    // VA uses special VA rate
    if (loanType === 'va') {
      return marketDataHook.marketData.interestRates.va_30_year;
    }
    
    // All others (conventional, FHA, USDA) use 30-year fixed
    return marketDataHook.marketData.interestRates['30_year_fixed'];
  };
  
  return (
    <MarketDataContext.Provider 
      value={{
        ...marketDataHook,
        getInterestRateForLoanType
      }}
    >
      {children}
    </MarketDataContext.Provider>
  );
};

export const useMarketData = () => {
  const context = useContext(MarketDataContext);
  if (!context) {
    throw new Error('useMarketData must be used within MarketDataProvider');
  }
  return context;
};
